import React, { useMemo } from 'react';
import styled from 'styled-components';

import GaugeContent from 'components/Gauge/GaugeContent';
import {
  LazyStudioEGauge,
  useIsEChartsRenderer,
} from 'components/StudioCharts';
import GqlClientProvider from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/GqlClientProvider';
import useMetric from 'hooks/useMetric';

import buildGaugeDefinition from './buildGaugeDefinition';
import ChartPlaceholder from './ChartPlaceholder';

// Fills the block, as a chart does, so Highcharts gets an explicit size.
// Gauges in unselected blocks ignore the pointer, like charts (StudioChart).
const GaugeBody = styled.div<{ $isInteractive: boolean }>`
  position: relative;
  isolation: isolate;
  flex: 1;
  min-height: 0;

  > div {
    position: absolute;
    inset: 0;
    pointer-events: ${(props) => (props.$isInteractive ? 'auto' : 'none')};
  }
`;

// A Gauge block (or Composite element): its metric for one entity against
// its peer group, drawn by the legacy Gauge it saves as (or with ECharts,
// where Studio draws its charts with it), within the block's dates and
// filters.
// Stable while its settings don't change, so it doesn't refetch on a move.
const StudioGauge = ({
  content,
  name,
  isInteractive,
}: {
  content: Pick<Studio.Card, 'id' | 'metricIds' | 'description' | 'gauge'>;
  name: string;
  isInteractive: boolean;
}) => {
  const [metricId] = content.metricIds ?? [];
  const metric = useMetric(metricId);
  const isECharts = useIsEChartsRenderer();
  const { peerGroup, entity, isReversed, isGradient } = content.gauge ?? {};
  const built = useMemo(
    () =>
      buildGaugeDefinition({
        card: {
          metricIds: metricId ? [metricId] : [],
          description: content.description,
          gauge: { peerGroup, entity, isReversed },
        },
        id: content.id,
        name,
        metricDisplayName: metric ? metric.name : '',
      }),
    [
      content.description,
      content.id,
      entity,
      isReversed,
      metric,
      metricId,
      name,
      peerGroup,
    ],
  );

  if ('missing' in built) {
    return (
      <ChartPlaceholder
        cardId={content.id}
        visType="gauge"
        message={built.missing}
      />
    );
  }
  const gauge = built.chartDefinition;
  return (
    <GaugeBody
      $isInteractive={isInteractive}
      data-testid={`studio-card-gauge-${content.id}`}
      data-renderer={isECharts ? 'echarts' : 'highcharts'}
    >
      <GqlClientProvider visName={gauge.name} visId={gauge.id} visType="Gauge">
        {isECharts ? (
          <LazyStudioEGauge gauge={gauge} isGradient={isGradient} />
        ) : (
          <GaugeContent gauge={gauge} />
        )}
      </GqlClientProvider>
    </GaugeBody>
  );
};

export default StudioGauge;
